import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const ProductCard = ({ product, index }) => {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className={`group relative overflow-hidden rounded-2xl transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <Link to="/sculpture" className="block">
        {/* Image Container */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-900">
          <img
            src={product.image}
            alt={product.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
          
          {/* Content */}
          <div className="absolute inset-0 p-6 flex flex-col justify-end">
            <div className="transform transition-all duration-500 group-hover:translate-y-0 translate-y-4">
              <p className="text-[hsl(45,100%,70%)] text-sm font-medium mb-2 tracking-wider uppercase">
                {product.category}
              </p>
              <h3 className="text-white text-2xl font-bold mb-2">
                {product.title}
              </h3>
              <p className="text-gray-300 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                {product.description}
              </p>
              
              {/* Price and Arrow */}
              <div className="flex items-center justify-between">
                <span className="text-white text-xl font-bold">
                  CHF {product.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <div className="w-10 h-10 rounded-full bg-[hsl(45,100%,70%)] flex items-center justify-center transform transition-all duration-300 group-hover:scale-110">
                  <ArrowRight className="w-5 h-5 text-black" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

const ProductGrid = ({ products, title, subtitle }) => {
  const titleRef = useRef(null);
  const [isTitleVisible, setIsTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsTitleVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => {
      if (titleRef.current) {
        observer.unobserve(titleRef.current);
      }
    };
  }, []);

  return (
    <section className="py-24 px-6 bg-black">
      <div className="container mx-auto max-w-7xl">
        {/* Section Title */}
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-1000 ${
            isTitleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {title.split(' ').map((word, i) => (
              <span key={i}>
                {word === 'luxury' || word === 'handcrafted' ? (
                  <span className="text-[hsl(45,100%,70%)]">{word}</span>
                ) : (
                  word
                )}
                {i < title.split(' ').length - 1 ? ' ' : ''}
              </span>
            ))}
          </h2>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>

        {/* "For more..." Link */}
        <div className="text-center mt-16">
          <Link
            to="/sculpture"
            className="inline-block text-[hsl(45,100%,70%)] text-2xl font-medium hover:scale-110 transition-transform duration-300 animate-blink-slow"
          >
            for more...
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes blink-slow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
        }
        
        .animate-blink-slow {
          animation: blink-slow 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default ProductGrid;